import mongoose, { Model, Schema } from 'mongoose';
import { IUser, NotificationPreferenceEnum, StatusEnum } from '../interfaces/auth.interface';
import { GenderEnum } from '../../Labour/interfaces/labour.interface';

const ObjectId = Schema.Types.ObjectId;

const userSchema = new Schema<IUser>(
    {
        email: {
            type: String,
            required: true,
            lowercase: true,
            match: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
        },
        roleId: {
            type: ObjectId,
            required: true,
            ref: 'Role',
        },
        failedLoginAttempts: {
            type: Number,
            default: 0,
        },
        lockUntil: {
            type: Boolean,
            default: null,
        },
        accountSetting: {
            userName: {
                type: String,
                required: false,
            },
            passwordHash: { type: String, minlength: 8, default: null },
            lastLogin: { type: Date, required: false },
        },
        joiningDate: { type: Date, default: Date.now },
        isVerifiedOtp: { type: Boolean, default: null },
        isVerifyOtpAt: { type: Date, default: null },
        isPasswordUpdate: { type: Boolean, default: false },
        passwordUpdatedAt: { type: Date, required: false },
        otp: { type: String, required: false },
        otpCreatedAt: { type: Date, required: false },
        otpExpiresAt: { type: Date, required: false },
        isDeleted: { type: Boolean, default: false },
        deletedAt: { type: Date, required: false },
        token: { type: String, required: false },
        createdBy: {
            type: ObjectId,
            ref: 'User',
        },
        // inviteStatus: {
        //     type: String,
        //     enum: Object.values(InviteStatusEnum),
        //     default: InviteStatusEnum.WAITING_TO_ACCEPT,
        // },
        invitedAt: { type: Date },
        acceptedInviteAt: { type: Date },
        firstName: { type: String },
        lastName: { type: String },
        phone: {
            type: String, trim: true,
            match: /^[\d-]{9,12}$/,
        },
        countryCode: {
            type: String,
            trim: true,
            match: /^\+\d{1,3}$/,
            default: null,
        },
        gender: { type: String, enum: Object.values(GenderEnum) },
        date_of_birth: { type: Date },
        notificationPreferences: {
            type: [String],
            enum: Object.values(NotificationPreferenceEnum),
            default: [NotificationPreferenceEnum.EMAIL, NotificationPreferenceEnum.IN_APP],
        },
        permissions: {
            type: [
                {
                    permissionId: { type: ObjectId, ref: 'Permissions' },
                    grantedBy: { type: ObjectId, ref: 'User' },
                },
            ],
            default: [],
        },
        hasAcceptedTerms: {
            type: Boolean,
            default: false,
        },
        restrictedPermissions: {
            type: [
                {
                    permissionId: { type: ObjectId, ref: 'Permissions' },
                    grantedBy: { type: ObjectId, ref: 'User' },
                },
            ],
            default: [],
        },
        forgotPassword: { type: String, required: false, default: null },
        forgotpasswordTokenExpiry: { type: Date, required: false, default: null },
        tokenExpiry: { type: Date, required: false },
        isFirstTimeResetPassword: { type: Boolean, default: null, required: false },
        sessionId: { type: String, required: false },
        refreshToken: { type: String, required: false },
        refreshTokenExpiry: { type: Date, required: false },
        isRememberMe: { type: Boolean, default: false },
        status: {
            type: String,
            enum: Object.values(StatusEnum),
            default: StatusEnum.WAITING_TO_ACCEPT,
        },
        profileImage: { type: ObjectId, ref: 'Attachment' },
        onboardingTime: { type: Date, default: null },
    },
    { timestamps: true, versionKey: false }
);
userSchema.pre('save', async function (next) {
    try {
        const user = this as IUser;
        if (!user.accountSetting?.userName) {
            user.accountSetting ??= {};
            user.accountSetting.userName = await generateUniqueUserName(user.email, this.$session());
        }
        next();
    } catch (error) {
        console.error('Error in User pre-save hook:', error);
        next(error);
    }
});

async function generateUniqueUserName(email: string, session?: mongoose.ClientSession): Promise<string> {
    let baseUserName = email.split('@')[0];
    let userName = baseUserName;
    let counter = 1;

    const UserModel = mongoose.model('User');
    while (await UserModel.exists({ 'accountSetting.userName': userName }).session(session)) {
        userName = `${baseUserName}${counter}`;
        counter++;
    }

    return userName;
}

userSchema.index({ email: 1, createdBy: 1 }, { unique: true });
userSchema.index({ createdBy: 1 });

const User: Model<IUser> = mongoose.model<IUser>('User', userSchema);
export default User;